import { Alert, Grid, Switch, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { TaskDataRowDataType, TaskRepetitionType, TaskTypeClassTextSize, TaskTypeClassWidthPrefix } from "ui/Scripts/source/models/enums";
import { ITaskTypeCol, TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { Translations } from "ui/Scripts/source/models/translations";
import { FormField } from "../../../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../../../framework/Grid/GridRow";
import { FormDesignerComponentPropertiesBold } from "./FormDesignerComponentPropertiesBold";
import { FormDesignerComponentPropertiesDefaultValue } from "./FormDesignerComponentPropertiesDefaultValue";
import { FormDesignerComponentPropertiesFontSize } from "./FormDesignerComponentPropertiesFontSize";
import { ILineColumnWidth, FormDesignerComponentPropertiesRow } from "./FormDesignerComponentPropertiesRow";
import { FormDesignerComponentPropertiesTextAlignment } from "./FormDesignerComponentPropertiesTextAlignment";
import { FormDesignerComponentPropertiesTextareaSize } from "./FormDesignerComponentPropertiesTextareaSize";
import { FormDesignerComponentPropertiesWidth } from "./FormDesignerComponentPropertiesWidth";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard";
import { FormInteger } from "source/components/framework/FormInteger";
import { FormDesignerComponentPropertiesNoTitleInRow } from "./FormDesignerComponentPropertiesNoTitleInRow";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { FormDesignerOptionsDialog } from "./FormDesignerOptionsDialog";
import { FormDesignerComponentPropertiesMinMax } from "./FormDesignerComponentPropertiesMinMax";
import { FormAccordion } from "source/components/framework/FormAccordion";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";
import { FormDesignerComponentPropertiesHeight } from "./FormDesignerComponentPropertiesHeight";
import { getInitializeFromRowTextFromExpression, FormDesignerInitializeFromRowDialog } from "./FormDesignerInitializeFromRowDialog";
import { FormDesignerComponentPropertiesExpression } from "./FormDesignerComponentPropertiesExpression";
import { FormDesignerComponentPropertiesName } from "./FormDesignerComponentPropertiesName";
import { FormDesignerComponentPropertiesVertical } from "./FormDesignerComponentPropertiesVertical";
import { ITaskTypeFieldPropUpdate } from "../../FormDesignerTypes";
import { FormDesignerComponentPropertiesTitlePage } from "./FormDesignerComponentPropertiesTitlePage";
import { FormDesignerComponentPropertiesDocRef } from "./FormDesignerComponentPropertiesDocRef";
import { FormDesignerComponentPropertiesRichText } from "./FormDesignerComponentPropertiesRichText";
import { ArrowLeftButton } from "source/components/framework/Button/ArrowLeftButton";
import { DocumentFolders } from "source/models/document/documentFolders";
import { FormDesignerComponentPropertiesEventRef } from "./FormDesignerComponentPropertiesEventRef";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { IdColorTitle, IIdColorTitle } from "ui/Scripts/source/models/common/idColorTitle";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { FormDesignerComponentPropertiesTaskRef } from "./FormDesignerComponentPropertiesTaskRef";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { IRecordCompanyFurtherEducationGroups } from "ui/Scripts/source/models/recordFurtherEducationGroup/recordCompanyFurtherEducationGroups";
import { FormDesignerComponentPropertiesRecordRef } from "./FormDesignerComponentPropertiesRecordRef";

interface IFormDesignerComponentPropertiesProps {
    companyId: string;
    taskRepetitionType: TaskRepetitionType;
    headerCols: ITaskTypeCol[];
    formCols: ITaskTypeCol[];
    lineCols: ITaskTypeCol[];
    headerTypeCol?: ITaskTypeCol;
    formTypeCol?: ITaskTypeCol;
    lineTypeCol?: ITaskTypeCol;
    lineColumnWidths: ILineColumnWidth[];
    formDataCol: ITaskDataRowCol;
    appReportDesignMode?: boolean;
    expanded?: boolean;
    //Event reference
    eventReports?: IIdTitleNode[];
    eventUnits?: IIdTitleNode[];
    eventStates?: IIdTitle[];
    eventUrgencyClasses?: IdColorTitle[];
    eventTypes?: IIdTitleNode[];
    eventTypeBs?: IIdTitleNode[];
    eventTypeCs?: IIdTitleNode[];
    //Task reference
    taskTypes?: ITaskEditTypeItem[];
    taskUnits?: IIdTitleNode[];
    taskStates?: IIdColorTitle[];
    //RecordReference related lists
    recordCharts?: IIdTitle[];
    recordUnits?: IIdTitleNode[];
    recordFurtherEducationGroups?: IRecordCompanyFurtherEducationGroups[];
    onHeaderItemClick: (field: string) => void;
    onFormItemClick: (field: string) => void;
    onTaskTypeHeaderPropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
    onTaskTypeFormPropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
    onTaskTypeLinePropUpdate: (values: ITaskTypeFieldPropUpdate[]) => void;
    onHeaderTaskDataRowValueUpdate?: (
        id: string,
        name: string,
        value: string | number | boolean,
    ) => void;
    onFormTaskDataRowValueUpdate?: (
        id: string,
        name: string,
        value: string | number | boolean,
    ) => void;
    onRemoveFieldFromLine: (id: string) => void;
    onAddFieldToLine: (id: string) => Promise<void>;
    onChangeLineColumnWidth: (id: string, classes: string) => void;
    onToggleExpand?: () => void;
    onGetDocumentFoldersAndDocuments?: (companyId: string) => Promise<DocumentFolders>;
}

export const FormDesignerComponentProperties = (
    props: IFormDesignerComponentPropertiesProps
) => {
    const [optionsDialogData, setOptionsDialogData] = useState<{ show: boolean, idOptions: IIdTitle[] }>({ show: false, idOptions: [] });

    const [showInializeFromRowDialog, setShowInializeFromRowDialog] = useState(false);
    const [defaultExpressionText, setDefaultExpressionText] = useState("");

    const taskTypeCol = props.headerTypeCol ?? props.formTypeCol ?? props.lineTypeCol;

    const isTextAreaComponent = taskTypeCol?.dataType === TaskDataRowDataType.String && taskTypeCol?.maxLength > 499;

    const isDropdown = taskTypeCol?.options?.length > 0 || taskTypeCol?.idOptions?.length > 0;

    const isLabel = taskTypeCol?.dataType === TaskDataRowDataType.Label;

    const isEmptyArea = taskTypeCol?.dataType === TaskDataRowDataType.Label && (taskTypeCol?.class ?? "").includes("emptyLabel");

    const isPageBreak = taskTypeCol?.dataType === TaskDataRowDataType.PageBreak;

    const isLineBreak = taskTypeCol?.dataType === TaskDataRowDataType.LineBreak;

    const isSignature = taskTypeCol?.dataType === TaskDataRowDataType.Signature;

    const isRadio = taskTypeCol?.dataType === TaskDataRowDataType.String && isDropdown && taskTypeCol?.class.includes("radio");

    const isPage = taskTypeCol?.dataType === TaskDataRowDataType.TitlePage;

    const isRichText = taskTypeCol?.dataType === TaskDataRowDataType.RichText;

    const isDocumentReference = taskTypeCol?.dataType === TaskDataRowDataType.DocumentReference;

    const isEventReference = taskTypeCol?.dataType === TaskDataRowDataType.EventReference;
    
    const isTaskReference = taskTypeCol?.dataType === TaskDataRowDataType.TaskReference;

    const isRecordReference = taskTypeCol?.dataType === TaskDataRowDataType.RecordReference;

    useEffect(() => {
        const cols = props.formTypeCol ? props.formCols : props.headerCols;
        if (taskTypeCol?.defaultExpression && cols) {
            setDefaultExpressionText(getInitializeFromRowTextFromExpression(taskTypeCol.defaultExpression, cols));
        } else {
            setDefaultExpressionText("");
        }
    }, [taskTypeCol?.defaultExpression ?? ""]);

    const onTaskTypePropUpdate = (values: ITaskTypeFieldPropUpdate[]) => {
        if (props.headerTypeCol) {
            props.onTaskTypeHeaderPropUpdate(values);
        } else {
            props.onTaskTypeFormPropUpdate(values);
        }
    };

    const onTaskDataRowValueUpdate = (id: string, name: string, value: string | number | boolean) => {
        if (props.headerTypeCol) {
            props.onHeaderTaskDataRowValueUpdate(id, name, value);
        } else {
            props.onFormTaskDataRowValueUpdate(id, name, value);
        }
    };

    const handleTextFieldChange = (event) => {
        onTaskTypePropUpdate([{
            field: taskTypeCol?.field,
            name: event.target.name,
            value: event.target.value
        }]);
    };

    const handleIntegerTextFieldChange = (name: string, value: number) => {
        onTaskTypePropUpdate([{
            field: taskTypeCol?.field,
            name,
            value
        }]);
    };

    const handleWidthChange = (field: string, classes: string) => {
        onTaskTypePropUpdate([{
            field,
            name: "class",
            value: classes
        }]);
    };

    const handleHeightChange = (field: string, classes: string) => {
        onTaskTypePropUpdate([{
            field,
            name: "class",
            value: classes
        }]);
    };

    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onTaskTypePropUpdate([{
            field: taskTypeCol?.field,
            name: event.target.name,
            value: event.target.checked
        }]);
    };

    const handleRequiredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onTaskTypePropUpdate([{
            field: taskTypeCol?.field,
            name: event.target.name,
            value: event.target.checked
        }]);
        //If boolean field input (= checking) is required, default value must be false
        if (taskTypeCol?.dataType === TaskDataRowDataType.Boolean && event.target.checked) {
            onTaskDataRowValueUpdate(taskTypeCol?.field, "valueStr", "0");
        }
    };

    const handleShowOptionsDialog = () => {
        setOptionsDialogData({
            show: true,
            idOptions: taskTypeCol?.idOptions ?? taskTypeCol?.options.map(i => IdTitle.createIdTitleSimple(i))
        });
    };

    const handleOptionsDialogClose = () => {
        setOptionsDialogData({
            show: false,
            idOptions: []
        });
    };

    const handleOptionsDialogSave = async (idOptions: IIdTitle[]) => {
        setOptionsDialogData((old) => ({ ...old, idOptions }));
        const values: ITaskTypeFieldPropUpdate[] = [
            { field: taskTypeCol?.field, name: "idOptions", value: idOptions },
        ];
        if (taskTypeCol?.defaultStr) {
            const defaultOption = idOptions.find(i => i.id === taskTypeCol.defaultStr);
            if (!defaultOption) {
                values.push({ field: taskTypeCol.field, name: "defaultStr", value: idOptions.length ? idOptions[0].id : "" });
            }
        }
        onTaskTypePropUpdate(values);
        handleOptionsDialogClose();
    };

    const handleShowInitializeFromRowDialog = () => {
        setShowInializeFromRowDialog(true);
    };

    const handleInializeFromRowDialogClose = () => {
        setShowInializeFromRowDialog(false);
    };

    const handleInializeFromRowDialogSave = async (defaultExpression: string) => {
        onTaskTypePropUpdate([{
            field: taskTypeCol?.field,
            name: "defaultExpression",
            value: defaultExpression
        }]);
        setShowInializeFromRowDialog(false);
    };

    const formFieldMargin = ".5rem 0 0 0";

    const isCalulatedField = TaskTypeCol.isCalculatedField(taskTypeCol);

    return (
        <DialogSectionCard
            headerTitle={Translations.FormProperties}
            contentPadding="16px 0 16px 0!important"
            buttons={props.onToggleExpand
                ? <ArrowLeftButton
                    onClick={props.onToggleExpand}
                    size="small"
                    variant="outlined"
                    rotateIcon={props.expanded}
                >
                    {!props.expanded ? Translations.Expand : Translations.Collapse}
                </ArrowLeftButton>
                : undefined}
        >
            <Grid sx={{
                overflowY: "auto",
                overflowX: "hidden",
                height: "calc(100vh - 350px)",
                paddingLeft: "0",
                paddingRight: "0"
            }}>
                {(!taskTypeCol
                    ? <div>
                        <Alert severity="info">{Translations.SelectComponentInFormToSeeComponentProperties}</Alert>
                    </div>
                    : <FormDesignerComponentPropertiesName
                        headerTypeCol={props.headerTypeCol}
                        formTypeCol={props.formTypeCol}
                        headerCols={props.headerCols}
                        formCols={props.formCols}
                        onHeaderItemClick={props.onHeaderItemClick}
                        onFormItemClick={props.onFormItemClick}
                    />
                )}
                {((!!props.headerTypeCol || !!props.formTypeCol) && !isPageBreak) &&
                    <>
                        <FormAccordion
                            defaultExpanded={true}
                            title={Translations.BasicInformation}
                        >
                            <GridRow>
                                {(!isLineBreak
                                    ? <>
                                        {!isPage &&
                                            <Grid item sm={12}>
                                                <FormDesignerComponentPropertiesWidth
                                                    classes={taskTypeCol.class}
                                                    formFieldMargin={0}
                                                    sizePrefix={
                                                        taskTypeCol.class.includes(TaskTypeClassWidthPrefix.Web2)
                                                            ? TaskTypeClassWidthPrefix.Web2
                                                            : TaskTypeClassWidthPrefix.Web
                                                    }
                                                    id={taskTypeCol.field}
                                                    title={Translations.FormWidth}
                                                    minimum={0}
                                                    onUpdate={handleWidthChange}
                                                />
                                            </Grid>
                                        }
                                        {!props.appReportDesignMode &&
                                            <>
                                                <Grid item sm={12}>
                                                    <FormDesignerComponentPropertiesWidth
                                                        classes={taskTypeCol.class}
                                                        formFieldMargin={0}
                                                        sizePrefix={TaskTypeClassWidthPrefix.Mobile}
                                                        id={taskTypeCol.field}
                                                        title={Translations.FormWidthMobile}
                                                        minimum={0}
                                                        onUpdate={handleWidthChange}
                                                    />
                                                </Grid>
                                                <Grid item sm={12}>
                                                    <FormDesignerComponentPropertiesWidth
                                                        classes={taskTypeCol.class}
                                                        formFieldMargin={0}
                                                        sizePrefix={TaskTypeClassWidthPrefix.Print}
                                                        id={taskTypeCol.field}
                                                        title={Translations.FormWidthPrint}
                                                        minimum={0}
                                                        onUpdate={handleWidthChange}
                                                    />
                                                </Grid>
                                            </>
                                        }
                                        {!isEmptyArea && !isPage && !isRichText && !isDocumentReference && !isEventReference && !isTaskReference && !isRecordReference &&
                                            <Grid item sm={12}>
                                                <FormField title={isLabel ? Translations.Text : Translations.Title} margin={formFieldMargin} required={isLabel}>
                                                    <TextField
                                                        name="title"
                                                        value={taskTypeCol.title}
                                                        onChange={handleTextFieldChange}
                                                        onBlur={(e) => {
                                                            if (!isLabel || !!e.target.value) return;
                                                            onTaskTypePropUpdate([{
                                                                field: taskTypeCol?.field,
                                                                name: e.target.name,
                                                                value: taskTypeCol?.field ?? Translations.Text
                                                            }]);
                                                        }}
                                                        minRows={isLabel ? 5 : 1}
                                                        multiline={isLabel}
                                                    />
                                                </FormField>
                                            </Grid>
                                        }
                                        {isSignature &&
                                            <Grid item sm={12}>
                                                <FormDesignerComponentPropertiesHeight
                                                    id={taskTypeCol.field}
                                                    classes={taskTypeCol.class}
                                                    formFieldMargin={0}
                                                    maximum={3}
                                                    title={Translations.FormHeight}
                                                    sizePrefix="signature"
                                                    onUpdate={handleHeightChange}
                                                />
                                            </Grid>
                                        }
                                        {isPage &&
                                            <FormDesignerComponentPropertiesTitlePage
                                                formFieldMargin={formFieldMargin}
                                                taskTypeCol={taskTypeCol}
                                                onTaskTypePropUpdate={onTaskTypePropUpdate}
                                            />
                                        }
                                        {isRichText &&
                                            <FormDesignerComponentPropertiesRichText
                                                formFieldMargin={formFieldMargin}
                                                taskTypeCol={taskTypeCol}
                                                onTaskTypePropUpdate={onTaskTypePropUpdate}
                                            />
                                        }
                                        {isDocumentReference &&
                                            <FormDesignerComponentPropertiesDocRef
                                                companyId={props.companyId}
                                                formFieldMargin={formFieldMargin}
                                                taskTypeCol={taskTypeCol}
                                                onGetDocumentFoldersAndDocuments={props.onGetDocumentFoldersAndDocuments}
                                                onTaskTypePropUpdate={onTaskTypePropUpdate}
                                            />
                                        }
                                        {isEventReference &&
                                            <FormDesignerComponentPropertiesEventRef
                                                companyId={props.companyId}
                                                formFieldMargin={formFieldMargin}
                                                taskTypeCol={taskTypeCol}
                                                formDataCol={props.formDataCol}
                                                eventReports={props.eventReports}
                                                eventUnits={props.eventUnits}
                                                eventStates={props.eventStates}
                                                eventUrgencyClasses={props.eventUrgencyClasses}
                                                eventTypes={props.eventTypes}
                                                eventTypeBs={props.eventTypeBs}
                                                eventTypeCs={props.eventTypeCs}
                                                onTaskTypePropUpdate={onTaskTypePropUpdate}
                                            />
                                        }
                                        {isTaskReference &&
                                            <FormDesignerComponentPropertiesTaskRef
                                                companyId={props.companyId}
                                                formFieldMargin={formFieldMargin}
                                                taskTypeCol={taskTypeCol}
                                                formDataCol={props.formDataCol}
                                                taskTypes={props.taskTypes}
                                                taskUnits={props.taskUnits}
                                                taskStates={props.taskStates}
                                                onTaskTypePropUpdate={onTaskTypePropUpdate}
                                            />
                                        }
                                        {isRecordReference &&
                                            <FormDesignerComponentPropertiesRecordRef
                                                companyId={props.companyId}
                                                formFieldMargin={formFieldMargin}
                                                taskTypeCol={taskTypeCol}
                                                formDataCol={props.formDataCol}
                                                recordCharts={props.recordCharts}
                                                recordUnits={props.recordUnits}
                                                recordFurtherEducationGroups={props.recordFurtherEducationGroups}
                                                onTaskTypePropUpdate={onTaskTypePropUpdate}
                                            />
                                        }
                                    </>
                                    : <Grid item sm={12}>
                                        <FormDesignerComponentPropertiesHeight
                                            id={taskTypeCol.field}
                                            classes={taskTypeCol.class}
                                            formFieldMargin={0}
                                            title={Translations.FormHeight}
                                            sizePrefix="lineBreak"
                                            onUpdate={handleHeightChange}
                                        />
                                    </Grid>)
                                }
                                {taskTypeCol.dataType === TaskDataRowDataType.String && isDropdown && (
                                    <Grid item sm={12}>
                                        <FormField title={Translations.FormOptions} margin={formFieldMargin}>
                                            <AppButton variant="outlined" fullWidth onClick={handleShowOptionsDialog}>
                                                {Translations.Edit}
                                            </AppButton>
                                        </FormField>
                                    </Grid>
                                )}
                                {isTextAreaComponent &&
                                    <Grid item sm={12}>
                                        <FormField title={Translations.FormTextAreaSize} margin={formFieldMargin}>
                                            <FormDesignerComponentPropertiesTextareaSize
                                                taskTypeCol={taskTypeCol}
                                                onChange={onTaskTypePropUpdate}
                                            />
                                        </FormField>
                                    </Grid>
                                }
                                {taskTypeCol.dataType === TaskDataRowDataType.Number &&
                                    <Grid item sm={12}>
                                        <FormField title={Translations.FormDecimals} margin={formFieldMargin}>
                                            <FormInteger
                                                max={8}
                                                min={0}
                                                maxLength={1}
                                                value={taskTypeCol.decimals ?? 0}
                                                required={true}
                                                onChange={(value) => {
                                                    onTaskTypePropUpdate([{
                                                        field: taskTypeCol.field,
                                                        name: "decimals",
                                                        value
                                                    }]);
                                                }}
                                            />
                                        </FormField>
                                    </Grid>
                                }
                                <FormDesignerComponentPropertiesExpression
                                    formFieldMargin={formFieldMargin}
                                    isRequired={true}
                                    show={TaskTypeCol.isCalculatedField(taskTypeCol)}
                                    field={taskTypeCol?.field}
                                    expression={taskTypeCol?.expression}
                                    expressionPropertyName="expression"
                                    expressionFormat={taskTypeCol?.expressionFormat}
                                    showExpressionFormat={taskTypeCol?.dataType === TaskDataRowDataType.Integer}
                                    taskTypeCols={props.formTypeCol ? props.formCols : props.headerCols}
                                    onTaskTypePropUpdate={onTaskTypePropUpdate}
                                />
                            </GridRow>
                        </FormAccordion>
                        {(!isLineBreak && !isEmptyArea && !isSignature && !isPage && !isRichText && !isDocumentReference && !isEventReference && !isTaskReference && !isRecordReference) &&
                            <FormAccordion
                                defaultExpanded={false}
                                title={Translations.Font}
                            >
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.FormFontSize} margin={formFieldMargin}>
                                            <FormDesignerComponentPropertiesFontSize
                                                taskTypeCol={taskTypeCol}
                                                sizeOptions={!isLabel
                                                    ? [
                                                        { class: TaskTypeClassTextSize.Small, title: Translations.FormSizeSmall },
                                                        { class: TaskTypeClassTextSize.Medium, title: Translations.FormSizeMedium },
                                                        { class: "", title: Translations.FormSizeDefault },
                                                        { class: TaskTypeClassTextSize.Large, title: Translations.FormSizeLarge },
                                                        { class: TaskTypeClassTextSize.Extra, title: Translations.FormSizeExtraLarge }
                                                    ]
                                                    : [
                                                        { class: "fontNormal", title: Translations.FormNormalText },
                                                        { class: "sLabel", class2: "fontBold", title: Translations.FormTitle4 },
                                                        { class: "mLabel", class2: "fontBold", title: Translations.FormTitle3 },
                                                        { class: "lLabel", class2: "fontBold", title: Translations.FormTitle2 },
                                                        { class: "eLabel", class2: "fontBold", title: Translations.FormTitle1 }
                                                    ]
                                                }
                                                onChange={onTaskTypePropUpdate}
                                            />
                                        </FormField>
                                    </Grid>
                                    {(isLabel ||
                                        (taskTypeCol.dataType === TaskDataRowDataType.String && !isRadio) ||
                                        taskTypeCol.dataType === TaskDataRowDataType.Number ||
                                        taskTypeCol.dataType === TaskDataRowDataType.Integer ||
                                        taskTypeCol.dataType === TaskDataRowDataType.Date ||
                                        taskTypeCol.dataType === TaskDataRowDataType.Time ||
                                        taskTypeCol.dataType === TaskDataRowDataType.TimeSpan) && (
                                        <>
                                            <Grid item sm={6}>
                                                <FormField title={Translations.FormBoldFont} margin={formFieldMargin}>
                                                    <FormDesignerComponentPropertiesBold
                                                        taskTypeCol={taskTypeCol}
                                                        onChange={onTaskTypePropUpdate}
                                                    />
                                                </FormField>
                                            </Grid>
                                            <FormDesignerComponentPropertiesTextAlignment
                                                formFieldMargin={formFieldMargin}
                                                taskTypeCol={taskTypeCol}
                                                onChange={(field: string, name: string, value: string | boolean) => {
                                                    onTaskTypePropUpdate([{ field, name, value }]);
                                                    if (props.onTaskTypeLinePropUpdate) {
                                                        props.onTaskTypeLinePropUpdate([{ field, name, value }]);
                                                    }
                                                }}
                                            />
                                        </>
                                    )}
                                </GridRow>
                            </FormAccordion>
                        }
                        {(!isLabel && !isLineBreak && !isEmptyArea && !isPage && !isRichText && !isDocumentReference && !isEventReference && !isTaskReference && !isRecordReference) &&
                            <FormAccordion
                                defaultExpanded={false}
                                title={Translations.AdditionalInformation}
                            >
                                <GridRow>
                                    {(taskTypeCol.dataType !== TaskDataRowDataType.Boolean &&
                                        taskTypeCol.dataType !== TaskDataRowDataType.Date &&
                                        taskTypeCol.dataType !== TaskDataRowDataType.Time &&
                                        !isCalulatedField &&
                                        !isSignature &&
                                        !isRadio
                                    ) && (
                                        <Grid item sm={12}>
                                            <FormField title={Translations.FormPlaceholder} margin={formFieldMargin}>
                                                <TextField
                                                    name="placeholder"
                                                    value={taskTypeCol.placeholder}
                                                    onChange={handleTextFieldChange}
                                                />
                                            </FormField>
                                        </Grid>
                                    )}
                                    {!isCalulatedField && !isSignature &&
                                        <Grid item sm={12}>
                                            <FormField title={Translations.FormDefaultValue} margin={formFieldMargin}>
                                                {(!taskTypeCol.defaultExpression
                                                    ? <FormDesignerComponentPropertiesDefaultValue
                                                        disabled={taskTypeCol.dataType === TaskDataRowDataType.Boolean && taskTypeCol.required}
                                                        required={taskTypeCol.required ?? false}
                                                        taskTypeCol={taskTypeCol}
                                                        onTaskTypePropUpdate={onTaskTypePropUpdate}
                                                    />
                                                    : <TextField
                                                        title={defaultExpressionText}
                                                        value={defaultExpressionText}
                                                    />
                                                )}
                                                {props.taskRepetitionType === TaskRepetitionType.Repeated &&
                                                    <Grid item sm={12} sx={{ marginTop: ".5rem" }}>
                                                        <AppButton variant="outlined" fullWidth onClick={handleShowInitializeFromRowDialog}>
                                                            {Translations.FormInitializeFromSavedRow}
                                                        </AppButton>
                                                    </Grid>
                                                }
                                            </FormField>
                                        </Grid>
                                    }
                                    {(taskTypeCol.dataType === TaskDataRowDataType.String && !isDropdown && !isSignature) &&
                                        <Grid item sm={6}>
                                            <FormField title={Translations.FormMaxTextLength} margin={formFieldMargin}>
                                                <FormInteger
                                                    max={isTextAreaComponent ? 9999 : 499}
                                                    min={isTextAreaComponent ? 500 : 1}
                                                    maxLength={isTextAreaComponent ? 4 : 3}
                                                    value={taskTypeCol.maxLength ?? 0}
                                                    required={true}
                                                    onChange={(value) => handleIntegerTextFieldChange("maxLength", value)}
                                                />
                                            </FormField>
                                        </Grid>
                                    }
                                    <FormDesignerComponentPropertiesMinMax
                                        formFieldMargin={formFieldMargin}
                                        taskTypeCol={taskTypeCol}
                                        onTaskTypePropUpdate={onTaskTypePropUpdate}
                                    />
                                    {(taskTypeCol.dataType === TaskDataRowDataType.Number || taskTypeCol.dataType === TaskDataRowDataType.Integer) &&
                                        <Grid item sm={6}>
                                            <FormField title={Translations.Unit} margin={formFieldMargin}>
                                                <TextField
                                                    name="unitTitle"
                                                    value={taskTypeCol.unitTitle}
                                                    InputProps={{ inputProps: { maxLength: 10 } } }
                                                    onChange={handleTextFieldChange}
                                                />
                                            </FormField>
                                        </Grid>
                                    }
                                    {!isCalulatedField && !isSignature &&
                                        <Grid item sm={6}>
                                            <FormField title={Translations.FormDisabled} margin={formFieldMargin}>
                                                <Switch
                                                    name="readOnly"
                                                    checked={taskTypeCol.readOnly || false}
                                                    disabled={!taskTypeCol.readOnly && taskTypeCol.required}
                                                    onChange={handleSwitchChange}
                                                />
                                            </FormField>
                                        </Grid>
                                    }
                                    {!isCalulatedField &&
                                        <Grid item sm={6}>
                                            <FormField title={Translations.FormRequired} margin={formFieldMargin}>
                                                <Switch
                                                    name="required"
                                                    checked={taskTypeCol.required || false}
                                                    disabled={taskTypeCol.readOnly && !taskTypeCol.required}
                                                    onChange={handleRequiredChange}
                                                />
                                            </FormField>
                                        </Grid>
                                    }
                                    {(taskTypeCol.dataType === TaskDataRowDataType.Boolean) &&
                                        <Grid item sm={6}>
                                            <FormField title={Translations.FormCompactStyle} margin={formFieldMargin}>
                                                <FormDesignerComponentPropertiesNoTitleInRow
                                                    taskTypeCol={taskTypeCol}
                                                    onChange={onTaskTypePropUpdate}
                                                />
                                            </FormField>
                                        </Grid>
                                    }
                                    {isRadio &&
                                        <Grid item sm={6}>
                                            <FormField title={Translations.FormVerticalDirection} margin={formFieldMargin}>
                                                <FormDesignerComponentPropertiesVertical
                                                    taskTypeCol={taskTypeCol}
                                                    onChange={onTaskTypePropUpdate}
                                                />
                                            </FormField>
                                        </Grid>
                                    }
                                    {TaskTypeCol.colCanBeUsedInFilter(taskTypeCol) && (
                                        <Grid item sm={6}>
                                            <FormField title={Translations.FormUsableInSearchFilter} margin={formFieldMargin}>
                                                <Switch
                                                    checked={taskTypeCol.useInFilter || false}
                                                    onChange={(e, checked) =>
                                                        onTaskTypePropUpdate([{
                                                            field: taskTypeCol.field,
                                                            name: "useInFilter",
                                                            value: checked
                                                        }])
                                                    }
                                                />
                                            </FormField>
                                        </Grid>
                                    )}
                                </GridRow>
                            </FormAccordion>
                        }
                    </>
                }
                {((!!props.onAddFieldToLine && !!props.onRemoveFieldFromLine && !!props.onChangeLineColumnWidth) &&
                  (!!props.formTypeCol || !!props.lineTypeCol) && !isLabel && !isLineBreak && !isEmptyArea) && (
                    <FormDesignerComponentPropertiesRow
                        formFieldMargin={formFieldMargin}
                        fieldName={taskTypeCol?.field}
                        formTypeCol={props.formTypeCol}
                        lineTypeCol={props.lineTypeCol}
                        lineCols={props.lineCols}
                        lineColumnWidths={props.lineColumnWidths}
                        onTaskTypePropUpdate={onTaskTypePropUpdate}
                        onRowLinePropUpdate={props.onTaskTypeLinePropUpdate}
                        onRemoveFieldFromLine={props.onRemoveFieldFromLine}
                        onAddFieldToLine={props.onAddFieldToLine}
                        onChangeLineColumnWidth={props.onChangeLineColumnWidth}
                    />
                )}
                {optionsDialogData.show &&
                    <FormDesignerOptionsDialog
                        isRadio={isRadio}
                        idOptions={optionsDialogData.idOptions}
                        onClose={handleOptionsDialogClose}
                        onSave={handleOptionsDialogSave}
                    />
                }
                {showInializeFromRowDialog &&
                    <FormDesignerInitializeFromRowDialog
                        cols={props.formTypeCol ? props.formCols : props.headerCols}
                        taskTypeCol={taskTypeCol}
                        onClose={handleInializeFromRowDialogClose}
                        onSave={handleInializeFromRowDialogSave}
                    />
                }
            </Grid>
        </DialogSectionCard>
    );
};
